import { useCallback, useEffect, useMemo } from "react";
import { useOptionalFullscreen } from "../../contexts/FullscreenContext";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import {
  $getRoot,
  $createRangeSelection,
  $setSelection,
  $isTextNode,
  $isElementNode,
} from "lexical";
import type { EditorState, LexicalEditor, SerializedEditorState } from "lexical";
import { FloatingToolbar } from "./FloatingToolbar";
import { AutoSavePlugin } from "./plugins/AutoSavePlugin";
import { SuggestionPlugin, type Suggestion } from "./plugins/SuggestionPlugin";
import { createEmptyEditorState, isValidSerializedEditorState } from "./utils/serialization";
import styles from "./RichTextEditor.module.css";

export interface RichTextEditorProps {
  initialContent?: SerializedEditorState | null;
  onChange?: (editorState: SerializedEditorState) => void;
  onAutoSave?: (
    editorState: SerializedEditorState,
    wordCount: number,
    characterCount: number,
  ) => void;
  suggestions?: Suggestion[];
  onAcceptSuggestion?: (suggestion: Suggestion) => void;
  onRejectSuggestion?: (suggestion: Suggestion) => void;
  placeholder?: string;
  readOnly?: boolean;
  autoSaveInterval?: number;
}

/**
 * Moves the cursor to the end of the document and focuses the editor.
 * Replaces AutoFocusPlugin + InitialContentPlugin to avoid the race condition
 * where AutoFocusPlugin focuses an empty editor before InitialContentPlugin
 * sets the real content, causing a full-document selection that leads to
 * data loss on the next keystroke (see #662).
 */
function FocusEndPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      const lastChild = root.getLastDescendant();
      if (lastChild) {
        const selection = $createRangeSelection();
        if ($isTextNode(lastChild)) {
          const endOffset = lastChild.getTextContentSize();
          selection.anchor.set(lastChild.getKey(), endOffset, "text");
          selection.focus.set(lastChild.getKey(), endOffset, "text");
        } else if ($isElementNode(lastChild)) {
          const childrenSize = lastChild.getChildrenSize();
          selection.anchor.set(lastChild.getKey(), childrenSize, "element");
          selection.focus.set(lastChild.getKey(), childrenSize, "element");
        }
        $setSelection(selection);
      }
    });
    editor.focus();
  }, [editor]);

  return null;
}

function EditorTheme() {
  return {
    paragraph: styles.paragraph,
    text: {
      bold: styles.textBold,
      italic: styles.textItalic,
      underline: styles.textUnderline,
      strikethrough: styles.textStrikethrough,
    },
    heading: {
      h1: styles.h1,
      h2: styles.h2,
      h3: styles.h3,
      h4: styles.h4,
    },
    list: {
      ul: styles.listUl,
      ol: styles.listOl,
      listitem: styles.listItem,
      nested: {
        listitem: styles.nestedListItem,
      },
    },
    quote: styles.quote,
    link: styles.link,
  };
}

export function RichTextEditor({
  initialContent,
  onChange,
  onAutoSave,
  suggestions = [],
  onAcceptSuggestion,
  onRejectSuggestion,
  placeholder = "Start writing...",
  readOnly = false,
  autoSaveInterval = 5000,
}: RichTextEditorProps) {
  // Validate the initial content - use empty state if content is invalid or missing
  const content =
    initialContent && isValidSerializedEditorState(initialContent)
      ? initialContent
      : createEmptyEditorState();

  const initialConfig = useMemo(
    () => ({
      namespace: "VersoEditor",
      theme: EditorTheme(),
      onError: (error: Error, _editor: LexicalEditor) => {
        console.error("Lexical error:", error);
      },
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, HorizontalRuleNode],
      editable: !readOnly,
      editorState: JSON.stringify(content),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- content is derived from initialContent which is stable per key
    [readOnly],
  );

  const handleChange = useCallback(
    (editorState: EditorState, _editor: LexicalEditor) => {
      if (onChange) {
        const serialized = editorState.toJSON();
        onChange(serialized);
      }
    },
    [onChange],
  );

  const { isFullscreen } = useOptionalFullscreen();

  return (
    <div className={styles.container}>
      <LexicalComposer initialConfig={initialConfig}>
        <div className={styles.editorWrapper}>
          <FloatingToolbar />
          <div className={styles.editorContent}>
            <RichTextPlugin
              contentEditable={<ContentEditable className={styles.contentEditable} />}
              placeholder={
                <div
                  className={`${styles.placeholder} ${isFullscreen ? styles.placeholderZen : ""}`}
                >
                  {isFullscreen ? "Just write…" : placeholder}
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <FocusEndPlugin />
            <HistoryPlugin />
            <ListPlugin />
            <HorizontalRulePlugin />
            <OnChangePlugin onChange={handleChange} />
            {onAutoSave && <AutoSavePlugin onSave={onAutoSave} interval={autoSaveInterval} />}
            {suggestions.length > 0 && (
              <SuggestionPlugin
                suggestions={suggestions}
                onAccept={onAcceptSuggestion}
                onReject={onRejectSuggestion}
              />
            )}
          </div>
        </div>
      </LexicalComposer>
    </div>
  );
}
